import { useState } from "react";
import { Plus, Minus } from "lucide-react";

const faqs = [
  {
    q: "Do we need to have experienced a breach or incident to work with you?",
    a: "No. The most valuable time to validate your AI security posture is before something goes wrong, when you still control the timeline, the remediation, and the narrative.",
  },
  {
    q: "How long does an assessment take?",
    a: "Phase 1 typically takes two to four weeks depending on the number of AI applications and the complexity of your environment. The discovery call surfaces a specific timeline scoped to your situation.",
  },
  {
    q: "Do you work with organizations that have not deployed AI yet?",
    a: "Yes. We help teams build a secure foundation before deployment. Our most common engagement is with organizations that have already shipped AI and want a clear picture of what's running, where the gaps are, and what the engineering path forward looks like.",
  },
  {
    q: "What industries do you serve?",
    a: "Healthcare and health IT organizations, manufacturers, and financial services firms where AI failure has real operational, regulatory, and financial consequences. If your AI deployment touches regulated data or regulated workflows, we work with organizations like yours.",
  },
  {
    q: "What is your pricing model?",
    a: "Engagements are scoped and priced based on the complexity of your AI environment and applicable regulatory frameworks, not hours or headcount. Flagship Assessments typically range $25k to $120k. Continuous retainers run $8k to $25k per month. Fractional advisory runs $4k to $10k per month. Every proposal is written and fixed-scope, benchmarked against the cost of a breach, not the cost of an assessment.",
  },
  {
    q: "Do you sign Business Associate Agreements for healthcare clients?",
    a: "Yes. We execute HIPAA-compliant Business Associate Agreements with all healthcare clients before any engagement begins.",
  },
  {
    q: "What makes ArqeraAI different from a compliance consultant or an automation agency?",
    a: "Compliance consultants document what you have. Automation agencies build what you ask for. ArqeraAI validates whether what you have is actually secure, fixes it if it is not, and gives you documented evidence, not paperwork.",
  },
  {
    q: "Do you work with agentic and autonomous AI systems?",
    a: "Yes. Agentic AI and operational controls are a core focus. We work on authorization boundaries, runtime validation, tool-access controls, human approval gates, and workflow isolation, the controls required to deploy autonomous workflows safely.",
  },
  {
    q: "Can you help if we are already under regulatory or audit pressure?",
    a: "Yes, and this is where the investment matters most. Organizations under regulatory pressure need engineering execution paced to their timeline, with documented evidence assembled as the work happens, ready to present to any regulator, auditor, or board member on demand.",
  },
];

function Item({ q, a, open, onClick }: { q: string; a: string; open: boolean; onClick: () => void }) {
  return (
    <div className="border-b border-arq-border">
      <button
        onClick={onClick}
        className="w-full text-left py-5 flex items-start justify-between gap-6 group"
      >
        <span className="font-body text-[16px] font-medium text-arq-text group-hover:text-arq-blue transition-colors">
          {q}
        </span>
        <span className="text-arq-blue flex-shrink-0 mt-1 transition-transform duration-200">
          {open ? <Minus className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
        </span>
      </button>
      {open && (
        <div className="pb-5 pr-10 text-arq-muted text-[15px] leading-[1.7]">{a}</div>
      )}
    </div>
  );
}

export default function ArqFAQ() {
  const [openIdx, setOpenIdx] = useState<number | null>(4);
  const half = Math.ceil(faqs.length / 2);
  const left = faqs.slice(0, half);
  const right = faqs.slice(half);

  const toggle = (i: number) => setOpenIdx(openIdx === i ? null : i);

  return (
    <section id="faq" className="bg-arq-surface1 py-20 md:py-[120px] px-6">
      <div className="max-w-[1200px] mx-auto">
        <div className="text-center mb-16">
          <div className="arq-label mb-6">Common Questions</div>
          <h2 className="font-display font-semibold text-arq-text" style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}>
            What to Expect
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-x-12">
          <div>
            {left.map((f, i) => (
              <Item key={i} q={f.q} a={f.a} open={openIdx === i} onClick={() => toggle(i)} />
            ))}
          </div>
          <div>
            {right.map((f, i) => (
              <Item key={i + half} q={f.q} a={f.a} open={openIdx === i + half} onClick={() => toggle(i + half)} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
